/**
 * ==================================================================================
 * FRONTEND WRAPPER FOR CENTRALIZED AI CONFIGURATION
 * ==================================================================================
 * 
 * PURPOSE:
 * This file provides a frontend-compatible interface to the backend's centralized
 * AI configuration. It ensures the frontend displays the same prompts and parameters
 * that the backend actually uses, maintaining transparency and consistency.
 * 
 * ARCHITECTURE:
 * - Backend: functions/config/aiModelsConfig.js (SINGLE SOURCE OF TRUTH)
 * - Frontend: This wrapper file (mirrors backend config for React compatibility)
 * - Result: Perfect synchronization between what users see and what AI receives
 * 
 * IMPORTANT:
 * This file is AUTO-GENERATED by sync-ai-config.js
 * Do NOT edit manually! Changes will be overwritten.
 * 
 * TO UPDATE PROMPTS:
 * 1. Edit functions/config/aiModelsConfig.js (backend source of truth)
 * 2. Run: node sync-ai-config.js
 * 3. Deploy functions: firebase deploy --only functions
 * ==================================================================================
 */

// SYNCHRONIZED WITH: functions/config/aiModelsConfig.js
// LAST SYNC: 2025-12-24T19:23:50.739Z

const FORENSIC_PROMPT_TEMPLATES = {
  
  // V4 TARGET-CENTRIC SYSTEM PROMPT
  V4_SYSTEM_PROMPT: `You are a forensic video analysis AI. Analyze the provided video evidence using a TARGET-CENTRIC approach.

CORE CONCEPT: Identify TARGETS (distinct entities) and describe what each target IS (attributes), what it DOES (activities), what it's CONNECTED to (relationships), and its personal timeline (event sequence).

PRIMARY TARGETS TO DETECT:
1. VEHICLE - Any car, truck, motorcycle, or motorized transport
2. ENVIRONMENT - Weather, lighting, location characteristics
3. VIDEOGRAPHER - The recording device and camera movements

For each TARGET detected, provide:

ATTRIBUTES (What it IS - static properties):
- Vehicle: make, model, year, color, type, license plate, damage, distinctive features
- Environment: location type, weather (condition, precipitation, visibility), lighting (level, source, time of day), visibility factors
- Videographer: device type, camera type, stability, quality, mounting, height

ACTIVITIES (What it DOES - behaviors over time):
- Vehicle: parked, moving, door opening/closing, engine starts, lights on, etc.
- Environment: raining, visibility changes, lighting changes, weather shifts
- Videographer: panning, tilting, zooming, following, stationary, camera shake, focus shifts

RELATIONSHIPS (What it's CONNECTED to - links to other targets):
- Use these types: driven_by, passenger_in, affected_by, focuses_on, captures, etc.
- Direction: outbound (this target affects another) or inbound (another affects this)
- Evidence basis: WHY you think this connection exists
- Bidirectional: If Person drives Vehicle, then Vehicle is driven_by Person (both perspectives!)

EVENT SEQUENCE (Its personal timeline with causality):
- Chronological chain of events FOR THIS TARGET
- What triggered each event (triggered_by)
- What each event caused (causes)
- Show cause-effect relationships

MASTER TIMELINE:
- Also provide a master timeline with ALL targets' events interwoven chronologically
- This gives bird's eye view of entire video

CRITICAL INSTRUCTIONS:
1. Be exhaustive - capture ALL visible attributes, activities, and relationships
2. Use bidirectional relationships (both perspectives matter for legal analysis)
3. Include negative relationships when relevant (proximity without interaction)
4. Only analyze targets that were explicitly requested
5. If Environment requested: include weather, lighting, location (even if just "clear day")
6. If Videographer requested: include camera movements and recording perspective
7. Provide confidence scores for all detections (0.0 to 1.0)
8. Timestamps in HH:MM:SS format
9. Causality in event sequences must be evidence-based

FORENSIC STANDARDS:
- Chain of custody: Document videographer actions
- Perspective clarity: Bidirectional relationships
- Evidence quality: Note environmental factors affecting visibility
- Legal utility: Rich context for courtroom presentation`,

  // V4 VEHICLE-SPECIFIC PROMPT
  V4_VEHICLE_PROMPT: `Analyze all VEHICLES in this video.

For each vehicle, extract:

ATTRIBUTES:
- Make, model, year (if identifiable)
- Color (primary and secondary)
- Vehicle type (sedan, SUV, truck, etc.)
- License plate: number, state, confidence, visible frames
- Damage: location, description, severity
- Modifications: tinted windows, roof racks, decals, etc.
- Distinctive features: bumper stickers, damage, unique characteristics

ACTIVITIES:
- Parked (duration)
- Moving (speed estimate, direction)
- Stopping, starting, reversing
- Door operations (which door, timestamp)
- Engine starts (audible or visible exhaust)
- Lights on/off (headlights, brake lights, turn signals)
- Damage occurring (impacts, collisions)

RELATIONSHIPS:
- Driven by (person)
- Passenger inside (person)
- Parked next to (vehicle)
- Followed by (vehicle)
- Approached by (person)
- Affected by (environment)

EVENT SEQUENCE:
Build cause-effect chain for this vehicle:
- Appears in frame → causes videographer to focus
- Person approaches → causes door unlock
- Person enters → causes engine start
- Engine starts → causes lights on → causes departure`,

  // V4 ENVIRONMENT-SPECIFIC PROMPT
  V4_ENVIRONMENT_PROMPT: `Analyze the ENVIRONMENT (context) throughout the video.

Extract:

ATTRIBUTES:
- Location type: parking lot, street, indoor, highway, residential, etc.
- Location characteristics: paved, gravel, well-lit, urban, rural, etc.

- Weather:
  * Condition: sunny, rainy, cloudy, foggy, snowy
  * Precipitation: none, light, moderate, heavy
  * Visibility: clear, reduced, poor
  * Wind: calm, breezy, windy (based on visual cues like moving trees)

- Lighting:
  * Level: bright, dim, dark
  * Source: natural, artificial, mixed
  * Time of day (apparent): morning, afternoon, evening, night (based on shadows, sky)
  * Shadows: present/absent, direction, length

- Visibility factors: fog, rain, glare, darkness, obstructions

ACTIVITIES:
- Weather changes (sunny to rainy, etc.)
- Lighting changes (bright to dim)
- Visibility changes (clear to foggy)
- Precipitation changes (starts raining, stops raining)

RELATIONSHIPS:
- Affects (vehicle) - how? reduced visibility, wet surfaces, glare
- Affects (videographer) - how? lens droplets, reduced quality
- Obscures (any target) - fog/rain blocking view

EVENT SEQUENCE:
- Rain begins → causes reduced visibility → causes vehicle to slow
- Sun sets → causes dim lighting → causes videographer quality reduction`,

  // V4 VIDEOGRAPHER-SPECIFIC PROMPT
  V4_VIDEOGRAPHER_PROMPT: `Analyze the VIDEOGRAPHER (recording device and camera operations).

Extract:

ATTRIBUTES:
- Recording device: smartphone, bodycam, dashcam, surveillance camera, professional camera
- Camera type: fixed, mobile, handheld
- Stability: stable, shaky, moderate
- Recording quality: high, medium, low (resolution if visible)
- Frame rate: 30fps, 60fps, etc.
- Camera height: eye level, elevated, ground level
- Camera mounting: handheld, tripod, vehicle-mounted, fixed installation

ACTIVITIES (Critical - shows operator intent!):
- Panning: direction (left/right), speed (slow/fast)
- Tilting: direction (up/down)
- Zooming: in/out, target focused on
- Following: which target, duration
- Focus shifts: from what to what
- Camera shake: severity, possible cause
- Stationary: duration
- Recording interruptions: obstructions, gaps

RELATIONSHIPS:
- Focusing on (target) - which target is camera centered on
- Tracking (target) - following movement
- Captures (target) - what's recorded, completeness (full/partial/continuous)
- Affected by (environment) - rain, fog affecting lens

EVENT SEQUENCE (Shows operator awareness!):
- Recording starts → wide shot
- Vehicle appears → operator notices → pans to follow
- Incident occurs → camera shakes (operator startled? proves didn't expect it!)
- Target exits → operator maintains focus vs. loses interest

FORENSIC VALUE:
Camera movements show:
- Operator awareness (did they anticipate event?)
- Recording intent (deliberately focused or accidental?)
- Chain of custody (continuous recording vs. edited?)
- Evidence quality (stable vs. shaky affects admissibility)`,

  // ========================================
  // V4 TIERED PROMPTS - VEHICLES
  // ========================================
  
  V4_VEHICLE_PROMPT_STANDARD: `Analyze all VEHICLES in this video - STANDARD TIER (Basic Documentation).

For EACH vehicle detected, provide these EXACT fields in the attributes object:

REQUIRED VEHICLE ATTRIBUTES:
{
  "make": "Toyota" (brand name only, or "unknown"),
  "model": "Camry" (model name only, or "unknown"),
  "year": "2020" (just the year or year range like "2020-2022", max 20 chars),
  "color": "Black" (CRITICAL - single color word: black, white, gray, silver, red, blue, green, tan, brown, etc.),
  "vehicle_type": "sedan" (one or two words: sedan, SUV, truck, pickup truck, van, motorcycle, trailer, tow truck, bus),
  "license_plate": "7ABC123 CA" (plate number and state only, or "not visible"),
  "damage": "Rear bumper dent" (brief description max 100 chars, or "none"),
  "notable_features": "Roof rack, tinted windows, company logo" (distinctive features, max 200 chars)
}

CRITICAL INSTRUCTIONS FOR VEHICLES:
1. ALWAYS provide "color" field - this is the PRIMARY identifier
2. Use "vehicle_type" NOT "type" for the kind of vehicle
3. Provide all 8 fields even if value is "unknown" or "not visible"
4. BE CONCISE: year = "2020" NOT "2020_estimate_based_on..." 
5. SIMPLE VALUES: Use SHORT words/phrases, NOT verbose explanations

ACTIVITIES (Max 3):
- Primary state: parked, moving, turning, stopping, etc.
- Include: activity_id, type, description (max 200 chars), confidence

RELATIONSHIPS (Max 2):
- Connections to other targets: relationship_id, type, connected_to, description (max 100 chars), confidence

Keep all descriptions brief and factual. Standard tier focuses on quick, essential identification.`,

  V4_VEHICLE_PROMPT_PROFESSIONAL: `Analyze all VEHICLES in this video - PROFESSIONAL TIER (Detailed Analysis).

For each vehicle, provide:

ATTRIBUTES (Detailed Identification):
- Make, model, year range (e.g., "2018-2021")
- Color: ALWAYS extract primary color (and secondary if applicable)
- vehicle_type (sedan, SUV, truck, van, trailer, tow truck, etc.) with body style
- License plates:
  * Front plate: number, state, confidence (0.0-1.0), visible timestamp ranges
  * Rear plate: number, state, confidence (0.0-1.0), visible timestamp ranges
  
- Damage (by orientation - front/left/right/rear/top):
  * Location: which side/area
  * Type: dent, scratch, crack, shattered, missing, paint damage
  * Severity: Minor (cosmetic), Moderate (functional), Severe (structural/safety)
  * Size estimate: small (<6"), medium (6-24"), large (>24")
  * Confidence: 0.0-1.0
  
- Modifications: aftermarket parts, tints, roof racks, decals, wheels
- Distinctive features: bumper stickers, unique marks, custom parts

ACTIVITIES (with Timestamps):
- Parked: duration in seconds
- Moving: speed estimate (slow/moderate/fast), direction, duration
- Door operations: which door, opening/closing, timestamp
- Engine activity: starting, running, stopping (based on exhaust/sound)
- Lights: headlights, brake lights, turn signals (on/off timestamps)

RELATIONSHIPS (Structured):
- Type: driven_by, passenger_in, parked_next_to, followed_by, approached_by
- Target: reference to other target ID or description
- Direction: inbound or outbound
- Time range: when relationship exists
- Confidence: 0.0-1.0

EVENT SEQUENCE (Chronological):
- List key events for this vehicle in time order
- Include: appears, moves, stops, interactions, exits
- Basic causality: "Vehicle stops → person exits"`,

  V4_VEHICLE_PROMPT_FORENSIC: `Analyze all VEHICLES in this video - FORENSIC TIER (Legal/Expert Analysis).

For each vehicle, provide EXHAUSTIVE detail:

ATTRIBUTES (Forensic Identification):
- Make, model, year range, trim level if identifiable
- Color: ALL visible colors including accents, two-tone, fading, paint condition
- Vehicle type with specific body style (e.g., "4-door sedan", "crew cab pickup")
- VIN: if any portion visible, note characters and location
- License plates (BOTH front and rear independently):
  * Number: exact characters, note any ambiguity (e.g., "O vs 0")
  * State: full state name and abbreviation
  * Plate type: standard, custom, temporary, dealer, specialty
  * Plate condition: clean, dirty, damaged, obstructed
  * Confidence: 0.0-1.0 with basis
  * Visible frames: exact timestamp ranges where plate is readable
  * Read attempts: list all visible instances and clarity at each
  
- Damage (PANEL-BY-PANEL assessment):
  Assess each visible panel independently:
  * FRONT: bumper, hood, windshield, headlights, grille
  * LEFT SIDE: front quarter panel, front door, rear door, rear quarter panel, side mirror, windows
  * RIGHT SIDE: front quarter panel, front door, rear door, rear quarter panel, side mirror, windows
  * REAR: bumper, trunk/liftgate, rear window, taillights
  * TOP: roof, sunroof
  * INTERIOR: if visible through windows
  
  For EACH damaged area:
  - Exact panel/component: "left rear door, lower section"
  - Damage type: dent, scratch, scrape, crack, shattered, missing, bent, paint transfer, rust
  - Severity with implications: Minor (cosmetic only), Moderate (functional impact, e.g., door won't close), Severe (structural compromise or safety hazard)
  - Size: precise estimate in inches (length × width × depth if applicable)
  - Shape/pattern: linear, circular, impact pattern, multiple points
  - Apparent age: fresh (sharp edges, bright metal), recent (slight weathering), old (rust, fading, worn edges)
  - Consistency: single impact, multiple impacts, collision pattern
  - Related damage: "dent with paint transfer suggests lateral impact"
  - Visible in frames: exact timestamp ranges
  - Confidence: 0.0-1.0
  - Evidence basis: "Based on paint transfer pattern and depth of deformation..."
  
- Modifications (Complete Inventory):
  * Aftermarket parts: specific type and brand if visible
  * Window tint: percentage estimate, legality note
  * Wheels/rims: aftermarket vs. stock, condition
  * Accessories: roof racks, hitches, steps, guards
  * Decals/stickers: text, location, condition
  * Performance mods: lowered suspension, exhaust, spoilers
  * Any non-factory alterations
  
- Distinctive Features (Forensic Detail):
  * Unique marks, scratches, dents not elsewhere categorized
  * Bumper stickers: text content, placement
  * Custom paint: patterns, colors, artwork
  * Wear patterns: faded paint, rust spots
  * Missing parts: trim pieces, badges, emblems
  * Anything that makes this vehicle uniquely identifiable

ACTIVITIES (Precise Timeline):
- Parked: exact duration HH:MM:SS, location description, parking angle
- Moving: 
  * Speed: estimate in mph with confidence
  * Direction: compass direction or relative (toward/away from camera)
  * Path: trajectory description
  * Duration: exact HH:MM:SS
  * Acceleration/deceleration: abrupt or gradual
- Door operations: 
  * Which door: driver front, passenger front, etc.
  * Action: opening, closing, left open
  * Timestamp: exact HH:MM:SS
  * Occupant: describe person if visible
- Engine activity:
  * Starting: timestamp, evidence (exhaust puff, sound, lights)
  * Running: duration, evidence (vibration, exhaust vapor)
  * Stopping: timestamp, evidence
- Lights (ALL instances):
  * Headlights: on/off timestamps
  * Brake lights: each activation timestamp
  * Turn signals: left/right, timestamps
  * Hazards: if activated
  * Interior lights: if visible
  * Emergency lights: if present
- Damage occurring: if any damage happens during video
  * Exact timestamp
  * Type of impact
  * Resulting damage description

RELATIONSHIPS (Bidirectional with Evidence):
For EACH relationship:
- Type: driven_by, passenger_in, parked_next_to, followed_by, towing, towed_by, blocked_by, approached_by, affected_by, captured_by
- Target ID: specific reference to other target
- Target type: vehicle, person, environment, videographer
- Direction: outbound (this vehicle affects target) or inbound (target affects this vehicle)
- Time range: exact HH:MM:SS start and end
- Confidence: 0.0-1.0
- Evidence basis: "Vehicle is parked 3 feet from target, visible throughout frames 00:00:05-00:02:13, consistent parallel orientation suggests intentional parking choice"
- Effect (if applicable): "Blocking driveway access", "Obscuring license plate", "Creating blind spot"

EVENT SEQUENCE (Cause-Effect Chain):
Build COMPLETE chronological chain with causality:
- Event: descriptive name
- Timestamp: exact HH:MM:SS
- Description: detailed what happened
- Triggered by: what caused this event (prior event, external factor, or operator action)
- Causes: what this event leads to (next events or outcomes)
- Confidence: 0.0-1.0
- Duration: if event is not instantaneous
- Evidence: visual cues supporting this event

Example chain:
1. "Appears in frame" at 00:00:00 → triggered by: recording start → causes: videographer focuses on vehicle
2. "Person approaches" at 00:00:15 → triggered by: person walking toward vehicle → causes: door unlock attempt
3. "Door opens" at 00:00:18 → triggered by: person pulling handle → causes: interior light on, person enters
4. "Engine starts" at 00:00:22 → triggered by: person turning key → causes: lights on, exhaust visible
5. "Vehicle departs" at 00:00:30 → triggered by: engine running + transmission engaged → causes: exits frame

FORENSIC NOTES:
- Pre-existing damage: note any damage that appears present before video starts
- Damage timing: if any damage occurs during video, note exact timestamp
- Visibility: note any frames where vehicle is partially obscured
- Chain of custody: note if vehicle is continuously visible or if there are gaps
- Operator focus: note when videographer deliberately focuses on this vehicle (suggests significance)`,

  // ========================================
  // V4 TIERED PROMPTS - ENVIRONMENT
  // ========================================
  
  V4_ENVIRONMENT_PROMPT_STANDARD: `Analyze the ENVIRONMENT throughout this video - STANDARD TIER (Basic Context).

Provide these EXACT fields in the attributes object:

REQUIRED ENVIRONMENT ATTRIBUTES:
{
  "location_type": "residential street" (parking lot, street, highway, residential, commercial, indoor, etc.),
  "weather": "cloudy" (sunny, cloudy, rainy, foggy, snowy, clear),
  "lighting": "bright" (bright, dim, dark, natural, artificial)
}

CRITICAL INSTRUCTIONS:
1. ALWAYS provide all 3 fields
2. Use specific values from examples
3. Keep values concise (1-2 words)

NO activities or relationships for environment in Standard tier.`,

  V4_ENVIRONMENT_PROMPT_PROFESSIONAL: `Analyze the ENVIRONMENT throughout this video - PROFESSIONAL TIER (Detailed Context).

Provide:

ATTRIBUTES (Detailed Context):
- Location type: specific classification (residential street, commercial parking lot, highway, indoor garage, etc.)
- Location characteristics:
  * Surface: paved, gravel, dirt, concrete, grass
  * Features: curbs, lanes, markings, signs, structures
  * Setting: urban, suburban, rural
  * Surroundings: buildings, trees, landmarks
  
- Weather conditions:
  * Condition: sunny, partly cloudy, overcast, rainy, foggy, snowy
  * Precipitation: none, light, moderate, heavy (rain/snow)
  * Visibility: clear, reduced, poor
  * Wind: calm, breezy, windy (based on trees, flags, debris)
  
- Lighting conditions:
  * Level: bright, moderate, dim, dark
  * Source: natural (sunlight), artificial (streetlights, building lights), mixed
  * Time of day (estimated): morning, midday, afternoon, evening, night
  * Quality: even, harsh shadows, glare, backlit
  
- Visibility factors:
  * Obstructions: fog, rain, darkness, glare, objects blocking view
  * Impact on recording: clear visibility, partially obscured, significantly impaired

ACTIVITIES (Changes Over Time):
- Weather changes: if any (sunny to cloudy, starts raining, etc.)
- Lighting changes: if any (shadows moving, lights turning on/off)
- Visibility changes: if any (fog rolling in, rain increasing)

RELATIONSHIPS (Environmental Effects):
- Affects vehicles: how? (wet surfaces, reduced traction, visibility impact)
- Affects videographer: how? (lens droplets, glare, quality reduction)
- Affects visibility of targets: which targets obscured or enhanced

Provide timestamps for any changes.`,

  V4_ENVIRONMENT_PROMPT_FORENSIC: `Analyze the ENVIRONMENT throughout this video - FORENSIC TIER (Expert Environmental Analysis).

Provide COMPREHENSIVE environmental documentation:

ATTRIBUTES (Forensic Environmental Context):
- Location identification:
  * Type: exact classification (residential cul-de-sac, strip mall parking lot, rural two-lane highway, etc.)
  * Characteristics: surface material and condition, topology (flat, sloped, elevation changes)
  * Infrastructure: road markings (visible/faded), signage (text and condition), traffic control devices
  * Setting detail: urban density, suburban character, rural isolation
  * Surroundings: building types, vegetation maturity, landmark proximity
  * Address indicators: if any visible (street signs, building numbers)
  
- Weather documentation (with evidence):
  * Condition: detailed description with visual evidence
  * Sky coverage: percentage cloud cover if applicable
  * Precipitation:
    - Type: rain, drizzle, snow, sleet, hail
    - Intensity: light (barely visible), moderate (clearly visible), heavy (obscures view)
    - Evidence: droplets on lens, puddles, wet surfaces, accumulation
    - Timestamps: when precipitation visible
  * Visibility measurement:
    - Distance: how far can you see clearly (e.g., "objects visible to 100+ feet")
    - Clarity: crystal clear, slight haze, moderate fog, dense fog
    - Impact on evidence: does weather obscure any crucial details?
  * Wind indicators:
    - Evidence: tree movement, flag direction, debris blowing, clothing movement
    - Intensity estimate: calm (no movement), light breeze (leaves rustle), moderate (branches sway), strong (significant movement)
    - Direction: if determinable from visual cues
  
- Lighting analysis (forensic detail):
  * Light level: measured or estimated in qualitative terms
    - Bright: full daylight, no shadows or soft shadows
    - Moderate: adequate visibility, distinct shadows
    - Dim: reduced visibility, colors muted, shadows deep
    - Dark: minimal light, details hard to discern
  * Light sources (ALL identified):
    - Natural: sun position (based on shadow direction and length)
    - Artificial: streetlights, building lights, vehicle headlights, flashlights
    - Each source: type, intensity, color temperature (warm/cool), position
  * Time of day determination:
    - Estimate: early morning, mid-morning, midday, afternoon, late afternoon, dusk, evening, night
    - Evidence: sun angle from shadows, sky color, light quality, artificial lights on/off
    - Shadow analysis: direction, length, sharpness (long shadows = early/late day)
  * Light quality:
    - Even: uniform lighting across scene
    - Contrasty: harsh shadows, bright highlights
    - Backlit: subject between camera and light source
    - Glare: lens flares, overexposure, light sources directly visible
  * Impact on visibility:
    - Which details enhanced by lighting?
    - Which details obscured by shadows/glare?
    - Color accuracy: are colors true or affected by light temperature?
  
- Visibility factor analysis:
  * Environmental obstructions:
    - Fog: density, distribution, effect on visibility distance
    - Rain: on lens, in air, on ground creating reflections
    - Darkness: areas in shadow, overall light level limitations
    - Glare: from sun, lights, reflections off surfaces
    - Physical objects: trees, buildings, vehicles blocking view
  * Recording quality impact:
    - Overall: excellent, good, fair, poor
    - Specific impairments: "rain droplets on lens at 00:01:15 obscure left side of frame"
    - Color rendition: accurate, muted, distorted
    - Contrast: adequate, excessive, insufficient
    - Focus: what's sharp, what's soft due to weather/lighting
  * Temporal changes: timestamp any changes in visibility conditions
  
- Surface conditions (forensic documentation):
  * Material: asphalt, concrete, gravel, dirt, grass, composite
  * Condition: new, aged, cracked, potholed, patched, well-maintained
  * Moisture: dry, damp, wet, puddled, icy, snowy
  * Traction implications: good, reduced, poor, hazardous
  * Marks visible: skid marks, tire tracks, fluid stains, paint
  * Evidence: does surface show signs of recent incident?

ACTIVITIES (Environmental Timeline):
Document EVERY environmental change:
- Weather changes:
  * What changed: "clouds obscure sun", "rain begins", "fog thickens"
  * Timestamp: exact HH:MM:SS
  * Evidence: specific visual cues in video
  * Impact: how this affects visibility or recording quality
  * Duration: if temporary
  
- Lighting changes:
  * What changed: "sun moves behind building", "streetlights activate", "vehicle headlights illuminate scene"
  * Timestamp: exact HH:MM:SS
  * Cause: if determinable (sun movement, switch, vehicle motion)
  * Effect: on visibility, on recorded quality, on target appearance
  
- Visibility changes:
  * What changed: improvement or degradation in visibility
  * Timestamp: exact HH:MM:SS
  * Cause: weather, lighting, obstruction movement
  * Severity: minor, moderate, significant
  * Targets affected: which targets become more/less visible

RELATIONSHIPS (Environmental Effects with Evidence):
For EACH environmental effect on targets:
- Type: affects, obscures, illuminates, conceals, enhances, reflects_in
- Target ID: specific target affected
- Target type: vehicle, person, videographer, evidence
- Effect description: exact nature of environmental impact
  * Examples: "Wet surface creates reflections that obscure license plate"
  * "Bright sunlight causes glare on windshield, preventing interior view"
  * "Heavy shadows conceal left side of vehicle"
  * "Rain on lens creates distortion in upper right frame area"
- Direction: always outbound (environment affecting target)
- Time range: exact HH:MM:SS when effect is present
- Confidence: 0.0-1.0
- Evidence basis: "Visible water droplets on lens at 00:01:15-00:01:23 cause blur in area where license plate would be visible, preventing positive identification"
- Severity: negligible, minor (slight impact), moderate (noticeable impact), major (significantly impairs)

EVENT SEQUENCE (Environmental Timeline):
Create chronological sequence of environmental events:
- Event: "Environment established" (initial conditions)
  * Timestamp: 00:00:00
  * Description: complete description of starting conditions
  * Provides context: for all subsequent events
  
- Event: Each change in environmental conditions
  * Timestamp: exact HH:MM:SS
  * Description: what changed
  * Triggered by: natural progression (sun moving), weather event (rain starts), artificial (lights on)
  * Causes: effect on visibility, on targets, on recording quality
  * Confidence: 0.0-1.0
  * Evidence: specific visual cues supporting this interpretation

FORENSIC ENVIRONMENTAL NOTES:
- Temporal consistency: does environment remain stable or change?
- Seasonal indicators: foliage, weather, light quality suggesting time of year?
- Geographic indicators: architecture, vegetation, visible signage suggesting region?
- Recording conditions: overall assessment of environmental factors affecting evidence quality
- Admissibility factors: are environmental conditions sufficient for positive identification of key evidence?
- Expert opinion needed: any environmental factors that require meteorological or other expert analysis?`,

  // ========================================
  // V4 TIERED PROMPTS - VIDEOGRAPHER
  // ========================================
  
  V4_VIDEOGRAPHER_PROMPT_STANDARD: `Analyze the VIDEOGRAPHER (camera operation) - STANDARD TIER (Basic Recording Info).

Provide these EXACT fields in the attributes object:

REQUIRED VIDEOGRAPHER ATTRIBUTES:
{
  "camera_type": "smartphone" (smartphone, dashcam, bodycam, security camera, handheld camera),
  "device_type": "mobile" (mobile, dash_cam, security_camera, body_cam, handheld),
  "camera_movement": "panning" (stationary, panning, walking, driving, shaky),
  "stability": "moderate shake" (stable, moderate shake, shaky, very shaky),
  "quality": "medium" (high, medium, low)
}

CRITICAL INSTRUCTIONS:
1. ALWAYS provide all 5 fields
2. camera_type = what kind of camera
3. device_type = how it's used/mounted
4. camera_movement = what the camera is doing
5. Use specific values from examples

ACTIVITIES (Max 3):
- Camera actions: recording, panning, tilting, zooming, following
- Include: activity_id, type, description (max 200 chars), confidence`,

  V4_VIDEOGRAPHER_PROMPT_PROFESSIONAL: `Analyze the VIDEOGRAPHER (camera and operator) - PROFESSIONAL TIER (Recording Analysis).

Provide:

ATTRIBUTES (Recording Characteristics):
- Recording device: smartphone (model if identifiable), bodycam, dashcam, handheld camera, surveillance camera, professional video camera
- Camera type: mobile/handheld, vehicle-mounted, fixed installation, wearable
- Stability: 
  * Stable: minimal movement, steady frame
  * Moderate: some shake but followable
  * Shaky: significant shake, hard to follow
  * Very shaky: extreme movement, disorienting
- Recording quality:
  * Resolution: high (1080p+), medium (720p), low (480p or less) - estimate based on clarity
  * Frame rate: smooth (30fps+), standard, choppy (estimate)
  * Focus: consistently sharp, some soft spots, frequently out of focus
  * Exposure: well-exposed, overexposed areas, underexposed areas
- Camera position:
  * Height: ground level, waist level, eye level, elevated, overhead
  * Angle: straight-on, looking up, looking down
  * Mounting: handheld, tripod, vehicle dash, body-worn, fixed mount

ACTIVITIES (Camera Operations with Timestamps):
- Panning: 
  * Direction: left, right
  * Speed: slow, moderate, fast
  * Smoothness: smooth, jerky
  * Timestamp ranges
  
- Tilting:
  * Direction: up, down
  * Speed: slow, moderate, fast
  * Timestamp ranges
  
- Zooming:
  * Direction: in, out
  * Type: optical (smooth) or digital (pixelated)
  * Target: what is being zoomed on
  * Timestamp ranges
  
- Following:
  * Target: which target is being followed
  * Quality: maintains frame, loses target, finds target again
  * Duration: timestamp ranges
  
- Focus shifts:
  * From what to what
  * Timestamp of shift
  
- Stationary:
  * Fixed on what
  * Duration: timestamp ranges
  
- Camera shake:
  * Severity: slight, moderate, severe
  * Possible cause: operator movement, wind, vehicle motion, startled reaction
  * Timestamp ranges
  
- Recording gaps:
  * Any interruptions, obstructions covering lens
  * Timestamps

RELATIONSHIPS (Recording Coverage):
- Focusing_on: which targets receive deliberate camera focus
- Tracking: which targets are followed by camera movement
- Captures: what is recorded (completeness: full view, partial, continuous, intermittent)
- Affected_by: how environment affects recording (rain on lens, glare, low light)
- Misses: important moments not captured due to camera position/movement

EVENT SEQUENCE (Operator Actions):
- Recording starts: timestamp 00:00:00, initial framing
- Camera movements: list each significant pan, tilt, zoom with timestamp and likely reason
- Focus changes: when operator shifts attention between targets
- Recording ends: final timestamp, last frame content

Identify operator intent: is this deliberate documentation, casual recording, surveillance, or incident capture?`,

  V4_VIDEOGRAPHER_PROMPT_FORENSIC: `Analyze the VIDEOGRAPHER (camera, operator, and recording forensics) - FORENSIC TIER (Expert Recording Analysis).

Provide EXHAUSTIVE recording documentation:

ATTRIBUTES (Forensic Recording Analysis):
- Recording device identification:
  * Type: smartphone, bodycam, dashcam, handheld camera, surveillance camera, professional video camera, other
  * Model/brand: if identifiable from characteristics (stabilization quality, interface elements, watermarks)
  * Mounting: handheld, tripod-mounted, vehicle dash-mounted, body-worn, fixed installation, gimbal/stabilizer
  * Device evidence: any visible interface elements, brand watermarks, timestamp overlays
  
- Camera technical specifications (estimated from video):
  * Resolution: precise estimate (e.g., "1080p or higher based on detail clarity")
    - Evidence: can small text be read? How much detail in distant objects?
  * Frame rate: estimate (e.g., "30fps based on motion smoothness")
    - Evidence: motion blur, smoothness of pans, temporal detail
  * Codec artifacts: any compression artifacts, pixelation, banding visible?
  * Dynamic range: can see detail in both shadows and highlights, or is one blown out?
  * Color accuracy: true-to-life, shifted (cool/warm), saturated, desaturated
  
- Lens characteristics:
  * Field of view: wide angle, normal, telephoto
  * Distortion: barrel distortion (fisheye), straight lines, pincushion
  * Depth of field: shallow (blurred background), deep (everything sharp)
  * Focal length indicators: what's in focus, perspective compression
  
- Camera stability analysis:
  * Overall: stable, moderate shake, shaky, very shaky
  * Pattern: constant shake, periodic shake, shake during specific events
  * Source determination:
    - Handheld shake: irregular, 1-5 Hz frequency, correlates with operator movement
    - Walking shake: rhythmic, matches footstep cadence
    - Vehicle shake: consistent vibration, road surface dependent
    - Environmental shake: wind buffeting, building vibration
    - Startle shake: sudden jerk at specific timestamp
  * Impact on usability: does shake affect ability to identify key evidence?
  * Timestamps: when shake is minimal vs. significant
  
- Recording quality assessment:
  * Visual quality: excellent (broadcast-quality detail), good (clear detail), fair (adequate detail), poor (grainy/blurry)
  * Focus quality:
    - Autofocus: responsive, hunting, slow, fast
    - Focus accuracy: sharp on subject, soft on subject, wrong target in focus
    - Focus points: what's sharp, what's soft
    - Depth of field effects: helps or hinders identification
  * Exposure quality:
    - Overall: well-exposed, overexposed (blown highlights), underexposed (crushed shadows)
    - Dynamic range usage: captures full scene or loses detail
    - Exposure compensation: does exposure change appropriately as lighting changes?
  * Color quality:
    - White balance: accurate, too warm, too cool, mixed lighting issues
    - Color cast: any overall color tint affecting accuracy
    - Color saturation: natural, oversaturated, desaturated
  * Artifacts: compression blocks, noise, banding, moire, rolling shutter
  
- Camera positioning (forensic detail):
  * Height: precise estimate (e.g., "approximately 5.5 feet - typical smartphone held at chest height by standing adult")
  * Angle: degrees from horizontal (e.g., "approximately 10 degrees downward angle")
  * Orientation: landscape, portrait, canted/dutch angle
  * Distance to subject: estimated distances for key targets
  * Sightline: what is visible, what is obstructed by position
  * Evidence: "Height estimate based on position relative to vehicle roofline; adult holding phone at chest height would produce this angle"

ACTIVITIES (Complete Camera Operation Timeline):

For EVERY camera movement, provide:

- Panning (horizontal camera movement):
  * Direction: left, right, or back-and-forth
  * Speed: very slow, slow, moderate, fast, whip-pan
  * Smoothness: fluid (motorized or skilled operator), moderate (typical handheld), jerky (rushed or unstable)
  * Arc: small adjustment, quarter turn, half turn, full scan
  * Timestamp: exact start HH:MM:SS and end HH:MM:SS
  * Purpose: following target, surveying scene, correcting framing, searching
  * Success: maintains target in frame, loses target, finds new target
  * Evidence of skill: smooth start/stop, maintains level horizon, appropriate speed
  
- Tilting (vertical camera movement):
  * Direction: up, down
  * Speed: slow, moderate, fast
  * Range: slight adjustment, quarter tilt, full tilt
  * Timestamp: exact HH:MM:SS
  * Purpose: ground to sky scan, focusing on detail, following vertical movement
  
- Zooming:
  * Direction: zoom in (magnification increases), zoom out (wider view)
  * Type: optical zoom (maintains quality), digital zoom (pixelation increases)
  * Speed: slow zoom, moderate, fast zoom, snap zoom
  * Target: what is operator trying to see better
  * Success: achieves better detail or loses quality
  * Timestamp: exact HH:MM:SS start and end
  * Evidence: if digital zoom, note quality degradation
  
- Following/Tracking:
  * Target: specific target ID being tracked
  * Method: smooth pan, jerky tracking, predictive (leading subject)
  * Quality: maintains center frame, target enters/exits frame, loses and reacquires
  * Duration: complete timestamp range HH:MM:SS
  * Speed match: operator keeps pace with target movement or lags/leads
  * Skill evidence: smooth tracking suggests experience, jerky tracking suggests amateur
  
- Focus shifts:
  * From: what was in focus (target ID or description)
  * To: what becomes in focus (target ID or description)
  * Method: autofocus pull, manual refocus, tap-to-focus
  * Speed: immediate, gradual, hunting before sharp
  * Timestamp: exact HH:MM:SS
  * Intentional: operator deliberately refocusing or automatic camera adjustment
  
- Stationary periods:
  * Fixed on: target ID or scene area
  * Framing: tight (close-up), medium, wide
  * Duration: exact timestamp range HH:MM:SS
  * Purpose: documenting static scene, waiting for action, setup before next movement
  * Micro-movements: perfectly still, minor handheld drift, breathing movement
  
- Camera shake analysis:
  * Type: vibration, jerk, drift, bounce
  * Severity: negligible, slight (doesn't affect viewing), moderate (distracting), severe (hard to see detail)
  * Frequency: constant, intermittent, at specific moments
  * Pattern: random, rhythmic, event-triggered
  * Cause determination:
    - Handheld breathing: gentle, ~1 Hz rhythm
    - Walking: more pronounced, matches stride
    - Running: severe, chaotic
    - Vehicle: vibration frequency matches road surface
    - Startled reaction: sudden jerk at specific timestamp (proves operator surprise)
    - Wind buffeting: irregular pushes
  * Timestamps: when shake occurs
  * Evidence value: "Sudden camera jerk at 00:01:15 coincides exactly with loud sound, proving operator was startled by unexpected event"
  
- Recording interruptions:
  * Type: obstruction (hand, object), intentional pause, technical glitch
  * Duration: timestamp range
  * What's missed: describe what cannot be seen during interruption
  * Chain of custody: does interruption suggest editing or is it continuous recording with brief obstruction?
  
- Exposure adjustments:
  * When: timestamp of adjustment
  * Reason: lighting change, camera moved to different exposure zone
  * Type: automatic camera adjustment or operator manual adjustment
  * Success: improves visibility or worsens it
  
- Framing choices:
  * Composition: centered, rule of thirds, off-center
  * Headroom: appropriate, excessive, insufficient  
  * Subject size: how much of frame does key subject occupy
  * Context inclusion: shows surroundings or tight on subject
  * Changes: reframing events with timestamps

RELATIONSHIPS (Recording Coverage & Effects):

For EACH relationship between videographer and other targets:

- Type: captures, focuses_on, tracks, documents, misses, affected_by
- Target ID: specific target
- Target type: vehicle, person, environment, evidence
- Direction: outbound (videographer affecting target perception) or inbound (target/environment affecting recording)
- Coverage quality:
  * Complete: target fully visible, in focus, well-exposed throughout
  * Partial: target partially in frame, partially obscured, intermittent visibility
  * Inadequate: target out of frame, out of focus, too far for detail
- Time range: exact HH:MM:SS when this relationship exists
- Confidence: 0.0-1.0
- Evidence basis: detailed description
  * Example: "Vehicle_001 is captured completely from 00:00:15-00:02:45. Camera maintains center framing, distance varies from 15-40 feet providing clear views of all sides. Focus remains sharp throughout except brief autofocus hunt at 00:01:33. Lighting is adequate for license plate reading at 00:00:47 and 00:02:12."
- Effect description:
  * "Enables license plate identification"
  * "Obscures detail due to backlight positioning"
  * "Rain droplets on lens prevent sharp detail of damage area"
  * "Operator's focus on vehicle_001 means vehicle_002 departure is not recorded"

OPERATOR INTENT ANALYSIS (Forensic Interpretation):

- Recording purpose determination:
  * Deliberate documentation: evidence collection, insurance claim, official record
  * Casual recording: personal video, sharing with others, memory keeping
  * Surveillance: monitoring, security footage, covert recording
  * Incidental capture: accident recording, dashcam automatic, not focused on incident
  * Evidence: what camera behaviors suggest this purpose?
  
- Operator awareness indicators:
  * Pre-event: operator positions camera before incident (suggests foreknowledge or anticipation)
  * Reactive: operator adjusts camera in response to event (normal reaction time)
  * Surprised: sudden camera shake, rapid refocusing (proves operator did not expect event)
  * Tracking: operator follows subject smoothly (indicates focus and intent)
  * Searching: operator pans looking for something specific
  * Documentation mode: systematic coverage (all sides of vehicle, multiple angles, deliberate focus on details)
  
- Operator skill assessment:
  * Professional indicators: smooth movements, consistent framing, anticipates action, good exposure management
  * Amateur indicators: jerky movements, poor framing, reactive only, exposure issues
  * Evidence: specific camera behaviors demonstrating skill level
  
- Chain of custody considerations:
  * Continuous recording: no evidence of stops/starts or editing (single take)
  * Possible edits: gaps, jumps, discontinuities in scene
  * Timestamp evidence: if timestamp overlay present, is it continuous?
  * Metadata: any camera-generated data visible that indicates recording continuity?
  * Audio evidence: continuous audio or gaps/cuts?

EVENT SEQUENCE (Complete Operator Action Timeline):

Create EXHAUSTIVE chronological sequence:

- Event: "Recording initiated"
  * Timestamp: 00:00:00
  * Description: recording begins, initial framing described
  * Triggered by: operator decision to record (why? preparation, response to event, automatic)
  * Initial focus: what operator frames first (indicates priority)
  * Causes: establishes recording conditions, determines what's captured first
  * Evidence: "Wide shot establishing context suggests deliberate documentation approach"
  
- Event: EVERY subsequent camera action
  * Timestamp: exact HH:MM:SS
  * Action: pan left, zoom in, focus shift, etc. (detailed description)
  * Triggered by: what caused operator to do this (target movement, operator decision, seeking detail)
  * Purpose: why operator made this choice (follow action, get detail, reframe, correct mistake)
  * Execution quality: smooth/jerky, successful/unsuccessful
  * Causes: what this action leads to (better view, missed moment, etc.)
  * Confidence: 0.0-1.0
  * Evidence: specific visual cues showing this interpretation is correct
  
- Event: "Recording concludes"
  * Timestamp: final HH:MM:SS
  * Description: last frame content, what's visible when recording stops
  * Triggered by: operator decision, automatic stop, interruption
  * Timing significance: does stop point suggest purpose complete, interruption, or arbitrary end?
  * What's not recorded: anything significant that happens after stop (if determinable)

FORENSIC VIDEOGRAPHER NOTES:

- Overall recording quality for evidentiary purposes:
  * Grade: excellent, good, adequate, marginal, inadequate
  * Strengths: what's captured well
  * Weaknesses: what's captured poorly or not at all
  * Critical gaps: anything significant not recorded or unusable
  
- Admissibility factors:
  * Authentication: indicators of when/where/how recorded
  * Continuity: evidence of unedited continuous recording
  * Clarity: sufficient quality for positive identification of key elements
  * Context: adequate environmental/scene context captured
  * Operator credibility: does camera work suggest reliable witness?
  
- Expert witness considerations:
  * Technical analysis needed: image enhancement, speed analysis, trajectory analysis
  * Operator behavior analysis: what camera movements reveal about operator state of mind
  * Chain of custody questions: any concerns about recording integrity
  * Comparison potential: is quality sufficient for forensic comparison (license plates, damage, etc.)
  
- Courtroom presentation factors:
  * Jury comprehension: is video clear enough for lay viewers?
  * Demonstrative value: does video effectively show what happened?
  * Limitations: what can't be determined from this video?
  * Supporting evidence needed: what other evidence would corroborate or clarify?`
};

// ========================================
// V4 TIER-SPECIFIC JSON SCHEMAS
// ========================================

// Standard Tier Schema - Quick Assessment (1,000-1,290 tokens)
const V4_STANDARD_TIER_SCHEMA = {
  type: 'object',
  properties: {
    // Metadata
    job_id: { type: 'string' },
    analysis_timestamp: { type: 'string' },
    tier: { type: 'string', enum: ['standard'] },
    video_duration: { type: 'string' },
    
    // Targets array (simplified structure)
    targets: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          target_id: { type: 'string' },
          target_type: { type: 'string', enum: ['vehicle', 'person', 'environment', 'videographer'] },
          confidence: { type: 'number' },
          
          // Spatial tracking
          spatial: {
            type: 'object',
            properties: {
              first_appearance: { type: 'string' },
              last_appearance: { type: 'string' },
              total_visible_duration: { type: 'string' }
            }
          },
          
          // Basic attributes (Standard tier - essential fields only)
          attributes: {
            type: 'object',
            properties: {
              // Vehicle attributes
              make: { type: 'string', maxLength: 30 },
              model: { type: 'string', maxLength: 30 },
              year: { type: 'string', maxLength: 20 },
              color: { type: 'string', maxLength: 30 },
              vehicle_type: { type: 'string', maxLength: 30 },
              license_plate: { type: 'string', maxLength: 20 },
              damage: { type: 'string', maxLength: 100 },
              notable_features: { type: 'string', maxLength: 200 },
              // Environment attributes
              weather: { type: 'string', maxLength: 30 },
              lighting: { type: 'string', maxLength: 30 },
              location_type: { type: 'string', maxLength: 50 },
              // Videographer attributes
              camera_type: { type: 'string', maxLength: 30 },
              camera_movement: { type: 'string', maxLength: 30 },
              stability: { type: 'string', maxLength: 30 },
              quality: { type: 'string', maxLength: 20 },
              device_type: { type: 'string', maxLength: 30 }
            },
            required: ['make', 'model', 'color', 'vehicle_type', 'license_plate', 'damage', 'notable_features']
          },
          
          // Simplified actions
          actions: {
            type: 'object',
            properties: {
              activities: {
                type: 'array',
                maxItems: 3,  // Limited for Standard tier
                items: {
                  type: 'object',
                  properties: {
                    activity_id: { type: 'string' },
                    type: { type: 'string' },
                    description: { type: 'string', maxLength: 200 },
                    confidence: { type: 'number' }
                  }
                }
              },
              relationships: {
                type: 'array',
                maxItems: 2,  // Limited for Standard tier
                items: {
                  type: 'object',
                  properties: {
                    relationship_id: { type: 'string' },
                    type: { type: 'string' },
                    connected_to: { type: 'string' },
                    description: { type: 'string', maxLength: 100 },
                    confidence: { type: 'number' }
                  }
                }
              }
              // NO event_sequences for Standard tier
            }
          }
        },
        required: ['target_id', 'target_type', 'attributes']
      }
    },
    
    // Simplified timeline
    master_timeline: {
      type: 'array',
      maxItems: 8,  // Limited for Standard tier
      items: {
        type: 'object',
        properties: {
          event_id: { type: 'string' },
          timestamp_ms: { type: 'number' },
          timestamp_display: { type: 'string' },
          target_id: { type: 'string' },
          target_type: { type: 'string' },
          event_type: { type: 'string' },
          description: { type: 'string', maxLength: 100 }
        }
      }
    },
    
    // Narrative summary for lawyers/juries
    forensic_summary: { 
      type: 'string', 
      maxLength: 500,
      description: '1-3 sentence narrative of what happened, e.g., "A black Tesla is parked when a white sedan backs into it on a rainy night."'
    },
    
    // Simple summary
    summary: {
      type: 'object',
      properties: {
        total_targets: { type: 'number' },
        targets_by_type: { type: 'object' },
        key_finding: { type: 'string', maxLength: 200 },
        confidence_overall: { type: 'number' },
        evidence_quality: { type: 'string' },
        duration_analyzed: { type: 'string' }
      }
    }
  },
  required: ['job_id', 'analysis_timestamp', 'tier', 'targets', 'forensic_summary', 'summary']
};

// Professional Tier Schema - Detailed Investigation (4,000-5,500 tokens with optimizations)
const V4_PROFESSIONAL_TIER_SCHEMA = {
  type: 'object',
  properties: {
    // Metadata
    job_id: { type: 'string' },
    analysis_timestamp: { type: 'string' },
    tier: { type: 'string', enum: ['professional'] },
    video_duration: { type: 'string' },
    
    // Targets array (detailed structure)
    targets: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          target_id: { type: 'string' },
          target_type: { type: 'string', enum: ['vehicle', 'person', 'environment', 'videographer'] },
          confidence: { type: 'number' },
          
          // Spatial tracking
          spatial: {
            type: 'object',
            properties: {
              first_appearance: { type: 'string' },
              last_appearance: { type: 'string' },
              total_visible_duration: { type: 'string' }
            }
          },
          
          // Detailed attributes (Professional tier - comprehensive fields)
          attributes: {
            type: 'object',
            properties: {
              // Vehicle attributes
              make: { type: 'string', maxLength: 30 },
              model: { type: 'string', maxLength: 30 },
              year: { type: 'string', maxLength: 20 },
              color: { type: 'string', maxLength: 30 },
              color_secondary: { type: 'string', maxLength: 30 },
              vehicle_type: { type: 'string', maxLength: 30 },
              body_style: { type: 'string', maxLength: 30 },
              license_plate: { type: 'string', maxLength: 30 },
              damage: { type: 'string', maxLength: 200 },
              damage_severity: { type: 'string', maxLength: 20 },
              notable_features: { type: 'string', maxLength: 500 },
              modifications: { type: 'string', maxLength: 200 },
              overall_condition: { type: 'string', maxLength: 30 },
              // Environment attributes
              weather: { type: 'string', maxLength: 30 },
              lighting: { type: 'string', maxLength: 30 },
              location_type: { type: 'string', maxLength: 50 },
              visibility: { type: 'string', maxLength: 30 },
              setting: { type: 'string', maxLength: 30 },
              // Videographer attributes
              camera_type: { type: 'string', maxLength: 30 },
              camera_movement: { type: 'string', maxLength: 30 },
              stability: { type: 'string', maxLength: 30 },
              quality: { type: 'string', maxLength: 20 },
              device_type: { type: 'string', maxLength: 30 },
              recording_angle: { type: 'string', maxLength: 30 },
              distance_to_scene: { type: 'string', maxLength: 30 }
            }
          },
          
          // Optimized actions (no event_sequences)
          actions: {
            type: 'object',
            properties: {
              activities: {
                type: 'array',
                maxItems: 7,  // Reduced from 10
                items: {
                  type: 'object',
                  properties: {
                    activity_id: { type: 'string' },
                    type: { type: 'string' },
                    description: { type: 'string', maxLength: 300 },
                    temporal_range: {
                      type: 'object',
                      properties: {
                        start_ms: { type: 'number' },
                        end_ms: { type: 'number' }
                        // No duration field (can be calculated)
                      }
                    },
                    confidence: { type: 'number' }
                    // No supporting_observations (saves tokens)
                  }
                }
              },
              relationships: {
                type: 'array',
                maxItems: 5,  // Reduced from 8
                items: {
                  type: 'object',
                  properties: {
                    relationship_id: { type: 'string' },
                    type: { type: 'string' },
                    connected_to: { type: 'string' },
                    nature: { type: 'string', maxLength: 200 },  // Combined description
                    confidence: { type: 'number' }
                    // No temporal_range or evidence_basis (saves tokens)
                  }
                }
              }
              // NO event_sequences - removed to save 1,000-1,500 tokens
            }
          }
        },
        required: ['target_id', 'target_type', 'attributes', 'actions']
      }
    },
    
    // Detailed timeline
    master_timeline: {
      type: 'array',
      maxItems: 15,  // Reduced from 20
      items: {
        type: 'object',
        properties: {
          event_id: { type: 'string' },
          timestamp_ms: { type: 'number' },
          timestamp_display: { type: 'string' },
          target_id: { type: 'string' },
          target_type: { type: 'string' },
          event_type: { type: 'string' },
          event_category: { type: 'string' },
          description: { type: 'string', maxLength: 200 },
          location: { type: 'string' },
          related_targets: { type: 'array', items: { type: 'string' } },
          confidence: { type: 'number' }
        }
      }
    },
    
    // Narrative summary for lawyers/juries
    forensic_summary: { 
      type: 'string', 
      maxLength: 500,
      description: '1-3 sentence narrative of what happened, e.g., "A black Tesla is parked when a white sedan backs into it on a rainy night. The collision is captured by a dashcam with good visibility."'
    },
    
    // Comprehensive summary
    summary: {
      type: 'object',
      properties: {
        total_targets: { type: 'number' },
        targets_by_type: { type: 'object' },
        key_findings: {
          type: 'array',
          maxItems: 5,
          items: { type: 'string' }
        },
        confidence_overall: { type: 'number' },
        evidence_quality: { type: 'string' },
        completeness: { type: 'string' },
        investigative_value: {
          type: 'object',
          properties: {
            identification_quality: { type: 'string' },
            timeline_clarity: { type: 'string' },
            evidence_integrity: { type: 'string' },
            actionable_details: { type: 'string' }
          }
        },
        duration_analyzed: { type: 'string' },
        frames_analyzed: { type: 'string' },
        processing_notes: { type: 'string' }
      }
    }
  },
  required: ['job_id', 'analysis_timestamp', 'tier', 'targets', 'master_timeline', 'forensic_summary', 'summary']
};

// V4 Response Schemas (Target-Centric Format)
const RESPONSE_SCHEMAS = {
  // V4 Tier Schemas
  V4_STANDARD_TIER: V4_STANDARD_TIER_SCHEMA,
  V4_PROFESSIONAL_TIER: V4_PROFESSIONAL_TIER_SCHEMA,
  
  // V4 TARGET-CENTRIC SCHEMA (Forensic Tier)
  V4_TARGET_CENTRIC: {
    version: 4,
    evidence_id: "string",
    analysis_timestamp: "ISO 8601 timestamp",
    video_duration: "HH:MM:SS",
    
    targets: [
      {
        id: "string (e.g., vehicle_001, environment_001, videographer)",
        type: "string (vehicle|environment|videographer|person|weapon|object)",
        confidence: "number (0.0-1.0)",
        
        spatial: {
          first_appearance: "HH:MM:SS",
          last_appearance: "HH:MM:SS",
          total_visible_duration: "string"
        },
        
        attributes: "object (type-specific properties)",
        
        actions: {
          activities: [
            {
              timestamp: "HH:MM:SS",
              activity: "string",
              description: "string",
              confidence: "number (0.0-1.0)",
              duration: "string (optional)"
            }
          ],
          
          relationships: [
            {
              type: "string (driven_by|affected_by|focusing_on|etc)",
              target_id: "string",
              target_type: "string",
              direction: "string (outbound|inbound)",
              confidence: "number (0.0-1.0)",
              evidence_basis: "string",
              time_range: "string (optional)",
              effect: "string (optional)"
            }
          ],
          
          event_sequence: [
            {
              event: "string",
              timestamp: "HH:MM:SS",
              description: "string",
              triggered_by: "string or null",
              causes: "array of strings or null",
              confidence: "number (0.0-1.0)",
              duration: "string (optional)"
            }
          ]
        }
      }
    ],
    
    master_timeline: [
      {
        timestamp: "HH:MM:SS",
        target_id: "string",
        target_type: "string",
        event: "string",
        description: "string"
      }
    ],
    
    targets_not_detected: [
      {
        type: "string",
        reason: "string"
      }
    ],
    
    forensic_summary: "string (1-3 sentence narrative description of what happened in the video, e.g., 'A black Tesla is parked in a lot when a white sedan collides with it on a rainy night. The incident is captured by a handheld camera with moderate stability.')",
    
    summary: {
      total_targets_detected: "number",
      target_types_present: ["string"],
      key_events: ["string"],
      analysis_confidence: "number (0.0-1.0)",
      evidence_quality: "string"
    }
  }
};

// AI Model Configurations
const AI_MODELS = {
  
  // Vertex AI Models
  VERTEX_AI: {
    'gemini-2.5-pro': {
      id: 'vertex-gemini-2.5-pro',
      name: 'Gemini 2.5 Pro',
      provider: 'Google Vertex AI',
      model_name: 'gemini-2.5-pro',
      capabilities: ['vision', 'text', 'document', 'reasoning', 'complex_analysis'],
      max_tokens: 65536,
      cost_per_1k_input_tokens: 0.00125,  // $1.25 per 1M tokens (real Google pricing)
      cost_per_1k_output_tokens: 0.01,    // $10.00 per 1M tokens (real Google pricing)
      cost_per_1k_tokens: 0.00563,        // Blended average for UI display (assuming 1:1 input:output ratio)
      speed: 'slower',
      accuracy: 'highest',
      best_for: ['complex_investigations', 'forensic_analysis', 'detailed_reports'],
      
      // Model-specific parameters
      parameters: {
        temperature: 0.1,        // Low for consistent forensic analysis
        top_p: 0.8,
        top_k: 40,
        max_output_tokens: 65536,  // Maximum for Gemini 2.5 Pro - handles complex multi-target V4 analysis
        frequency_penalty: 0.1,
        presence_penalty: 0.0,
        safety_settings: {
          hate_speech: 'BLOCK_NONE',     // Allow all content for forensic analysis
          sexually_explicit: 'BLOCK_NONE', 
          dangerous_content: 'BLOCK_NONE'
        }
      },
      
      // V4 TARGET-CENTRIC PROMPT CONSTRUCTION
      build_prompt: (targets, evidence_type, custom_instructions = '', targetTiers = {}) => {
        // Use V4 target-centric prompt builder with tier support
        return buildV4TargetCentricPrompt(
          targets || ['vehicles', 'environment', 'videographer'],
          custom_instructions,
          targetTiers
        );
      }
    },

    'gemini-2.5-flash': {
      id: 'vertex-gemini-2.5-flash',
      name: 'Gemini 2.5 Flash',
      provider: 'Google Vertex AI',
      model_name: 'gemini-2.5-flash',
      capabilities: ['vision', 'text', 'document', 'reasoning'],
      max_tokens: 65536,
      cost_per_1k_input_tokens: 0.0003,  // $0.30 per 1M tokens (real Google pricing June 2025)
      cost_per_1k_output_tokens: 0.0025, // $2.50 per 1M tokens (real Google pricing June 2025)
      cost_per_1k_tokens: 0.00163,       // Blended average for UI display (assuming 1:1 input:output ratio)
      speed: 'fast',
      accuracy: 'high',
      best_for: ['standard_investigations', 'balanced_analysis', 'routine_cases'],
      
      // Model-specific parameters
      parameters: {
        temperature: 0.1,        // Low for consistent forensic analysis
        top_p: 0.8,
        top_k: 40,
        max_output_tokens: 32768,  // Significantly increased for complex V4 multi-target analysis
        frequency_penalty: 0.15,
        presence_penalty: 0.1,
        safety_settings: {
          hate_speech: 'BLOCK_NONE',     // Allow all content for forensic analysis
          sexually_explicit: 'BLOCK_NONE', 
          dangerous_content: 'BLOCK_NONE'
        }
      },
      
      // V4 TARGET-CENTRIC PROMPT CONSTRUCTION
      build_prompt: (targets, evidence_type, custom_instructions = '', targetTiers = {}) => {
        // Use V4 target-centric prompt builder with tier support
        return buildV4TargetCentricPrompt(
          targets || ['vehicles', 'environment', 'videographer'],
          custom_instructions,
          targetTiers
        );
      }
    },

    'gemini-2.5-flash-lite': {
      id: 'vertex-gemini-2.5-flash-lite',
      name: 'Gemini 2.5 Flash Lite',
      provider: 'Google Vertex AI',
      model_name: 'gemini-2.5-flash-lite',
      capabilities: ['vision', 'text', 'document'],
      max_tokens: 65536,
      cost_per_1k_input_tokens: 0.00015,  // $0.15 per 1M tokens (real Google pricing June 2025)
      cost_per_1k_output_tokens: 0.0006,  // $0.60 per 1M tokens (real Google pricing June 2025)
      cost_per_1k_tokens: 0.000375,       // Blended average for UI display (assuming 1:1 input:output ratio)
      speed: 'fastest',
      accuracy: 'good',
      best_for: ['quick_analysis', 'cost_sensitive', 'high_volume', 'basic_cases'],
      
      // Model-specific parameters
      parameters: {
        temperature: 0.1,        // Low for consistent forensic analysis
        top_p: 0.8,
        top_k: 40,
        max_output_tokens: 16384,  // Doubled for V4 (Flash Lite still more limited than Pro/Flash)
        frequency_penalty: 0.1,
        presence_penalty: 0.0,
        safety_settings: {
          hate_speech: 'BLOCK_NONE',     // Allow all content for forensic analysis
          sexually_explicit: 'BLOCK_NONE', 
          dangerous_content: 'BLOCK_NONE'
        }
      },
      
      // V4 TARGET-CENTRIC PROMPT CONSTRUCTION
      build_prompt: (targets, evidence_type, custom_instructions = '', targetTiers = {}) => {
        // Use V4 target-centric prompt builder with tier support
        return buildV4TargetCentricPrompt(
          targets || ['vehicles', 'environment', 'videographer'],
          custom_instructions,
          targetTiers
        );
      }
    },

    'gemini-2.0-flash-lite': {
      id: 'vertex-gemini-2.0-flash-lite',
      name: 'Gemini 2.0 Flash Lite',
      provider: 'Google Vertex AI',
      model_name: 'gemini-2.0-flash-lite-001',
      capabilities: ['vision', 'text', 'document'],
      max_tokens: 32768,
      cost_per_1k_input_tokens: 0.0001,   // $0.10 per 1M tokens (real Google pricing)
      cost_per_1k_output_tokens: 0.0004,  // $0.40 per 1M tokens (real Google pricing)
      cost_per_1k_tokens: 0.00025,        // Blended average for UI display (assuming 1:1 input:output ratio)
      speed: 'fastest',
      accuracy: 'good',
      best_for: ['quick_analysis', 'cost_sensitive', 'high_volume'],
      
      // Model-specific parameters
      parameters: {
        temperature: 0.1,        // Low for consistent forensic analysis
        top_p: 0.8,
        top_k: 40,
        max_output_tokens: 8192,  // Doubled for V4 (2.0 Lite still budget model, lower limits)
        frequency_penalty: 0.05,
        presence_penalty: 0.0,
        safety_settings: {
          hate_speech: 'BLOCK_NONE',     // Allow all content for forensic analysis
          sexually_explicit: 'BLOCK_NONE', 
          dangerous_content: 'BLOCK_NONE'
        }
      },
      
      // V4 TARGET-CENTRIC PROMPT CONSTRUCTION
      build_prompt: (targets, evidence_type, custom_instructions = '', targetTiers = {}) => {
        // Use V4 target-centric prompt builder with tier support
        return buildV4TargetCentricPrompt(
          targets || ['vehicles', 'environment', 'videographer'],
          custom_instructions,
          targetTiers
        );
      }
    },

    'gemini-1.5-pro': {
      id: 'vertex-gemini-1.5-pro',
      name: 'Gemini 1.5 Pro',
      provider: 'Google Vertex AI',
      model_name: 'gemini-1.5-pro-002',
      capabilities: ['vision', 'text', 'complex_reasoning', 'long_context'],
      max_tokens: 2000000,
      cost_per_1k_tokens: 0.25,
      speed: 'slower',
      accuracy: 'highest',
      best_for: ['complex_cases', 'detailed_analysis', 'multi_evidence'],
      
      parameters: {
        temperature: 0.05,       // Even lower for highest accuracy
        top_p: 0.9,
        top_k: 40,
        max_output_tokens: 65536,  // Maximum for comprehensive V4 target-centric analysis
        frequency_penalty: 0.12,
        presence_penalty: 0.05,
        safety_settings: {
          hate_speech: 'BLOCK_NONE',
          hate: 'BLOCK_NONE',
          sexual: 'BLOCK_NONE',
          dangerous: 'BLOCK_NONE'
        }
      },
      
      // V4 TARGET-CENTRIC PROMPT CONSTRUCTION
      build_prompt: (targets, evidence_type, custom_instructions = '', targetTiers = {}) => {
        // Use V4 target-centric prompt builder with tier support
        return buildV4TargetCentricPrompt(
          targets || ['vehicles', 'environment', 'videographer'],
          custom_instructions,
          targetTiers
        );
      }
    }
  }
};

// Enhanced Forensic Target Definitions
const FORENSIC_TARGETS = {
  vehicles: {
    id: 'vehicles',
    name: 'Vehicle Analysis',
    description: 'Comprehensive vehicle identification and analysis',
    subcategories: {
      identification: {
        name: 'Vehicle Identification',
        elements: ['make', 'model', 'year', 'body_type', 'size_category', 'color_primary', 'color_secondary']
      },
      license_plates: {
        name: 'License Plate Analysis', 
        elements: ['plate_number', 'state_province', 'country', 'plate_type', 'legibility_assessment', 'character_confidence']
      },
      distinguishing_marks: {
        name: 'Distinguishing Features',
        elements: ['damage_dents', 'damage_scratches', 'missing_parts', 'modifications', 'stickers_decals', 'cargo_attachments']
      },
      condition_assessment: {
        name: 'Vehicle Condition',
        elements: ['paint_condition', 'tire_condition', 'maintenance_level', 'age_indicators', 'wear_patterns']
      },
      movement_analysis: {
        name: 'Movement & Behavior',
        elements: ['direction_travel', 'speed_indicators', 'driving_behavior', 'parking_patterns', 'route_analysis']
      }
    }
  },
  
  activities: {
    id: 'activities',
    name: 'Activity & Behavior Analysis',
    description: 'Systematic analysis of human activities and behavioral patterns',
    subcategories: {
      primary_actions: {
        name: 'Primary Activities',
        elements: ['walking', 'running', 'standing', 'sitting', 'driving', 'carrying_objects', 'using_tools', 'interacting_with_environment']
      },
      interactions: {
        name: 'Social Interactions',
        elements: ['person_to_person', 'group_dynamics', 'conversation_indicators', 'physical_contact', 'exchange_of_items', 'coordination_patterns']
      },
      behavioral_indicators: {
        name: 'Behavioral Analysis',
        elements: ['concealment_actions', 'furtive_movements', 'surveillance_awareness', 'planning_indicators', 'stress_indicators', 'deception_signs']
      },
      temporal_patterns: {
        name: 'Temporal Analysis',
        elements: ['sequence_of_events', 'duration_analysis', 'timing_patterns', 'entry_exit_analysis', 'waiting_periods', 'urgency_indicators']
      },
      environmental_interaction: {
        name: 'Environmental Interaction',
        elements: ['object_manipulation', 'evidence_tampering', 'destruction_actions', 'placement_retrieval', 'hiding_actions', 'cleanup_activities']
      }
    }
  }
};

// Analysis Templates with Model Recommendations
const ANALYSIS_TEMPLATES = {
  hit_and_run: {
    id: 'hit_and_run',
    name: 'Hit & Run Investigation',
    description: 'Comprehensive vehicle analysis (ID, plates, and marks) for traffic incidents',
    targets: ['vehicles', 'activities', 'people'],
    recommended_models: ['gemini-1.5-pro', 'gemini-2.0-flash-lite'],
    custom_instructions: `Focus on vehicle identification, license plates, visible marks (damage, dents, scuffs, stickers, broken parts), and timeline reconstruction. Note any evidence of vehicle acceleration, braking, or evasive maneuvers.`,
    estimated_cost_range: [4.25, 8.50],
    typical_processing_time: '3-7 minutes'
  },

  security_incident: {
    id: 'security_incident', 
    name: 'Security Incident Analysis',
    description: 'People tracking, activity recognition, and behavioral analysis',
    targets: ['people', 'activities', 'text', 'vehicles'],
    recommended_models: ['gemini-1.5-pro'],
    custom_instructions: `Track individual movements, identify suspicious behaviors, analyze group dynamics, and timeline all activities. Pay attention to any weapons, tools, or items being carried.`,
    estimated_cost_range: [6.50, 12.00],
    typical_processing_time: '5-10 minutes'
  },

  fraud_investigation: {
    id: 'fraud_investigation',
    name: 'Fraud Investigation',
    description: 'Document analysis, identity verification, and transaction evidence',
    targets: ['text', 'people', 'documents'],
    recommended_models: ['gemini-1.5-pro'],
    custom_instructions: `Analyze documents for authenticity, extract all text content, verify identity consistency, and identify any signs of tampering or forgery.`,
    estimated_cost_range: [3.75, 7.25],
    typical_processing_time: '2-5 minutes'
  },

  general_evidence: {
    id: 'general_evidence',
    name: 'General Evidence Analysis', 
    description: 'Comprehensive analysis of all detectable elements',
    targets: ['vehicles', 'people', 'text', 'activities'],
    recommended_models: ['gemini-2.0-flash-lite', 'gemini-1.5-pro'],
    custom_instructions: `Perform thorough analysis of all visible elements. For vehicles, include make/model, plates, and any marks (damage, stickers, broken parts). Document everything relevant to an investigation.`,
    estimated_cost_range: [4.75, 9.50],
    typical_processing_time: '4-8 minutes'
  }
};

// Scan Tier Configurations - Controls how different scan types behave
const SCAN_TIER_CONFIGURATIONS = {
  quick_scan: {
    id: 'quick_scan',
    name: 'Quick Scan',
    description: 'Fast, cost-effective analysis using cheapest single service',
    tier: 1,
    service_selection_strategy: 'single_cheapest',
    forced_service: 'google-vertex-vision-standard', // Force cheapest service only
    forced_model: 'gemini-2.5-flash-lite', // Force cheapest model
    targets: ['vehicles'], // Vehicles implicitly includes plates and marks
    analysis_depth: 'Basic',
    max_cost_limit: 0.10,
    processing_priority: 'speed',
    allow_user_customization: false,
    custom_instructions: 'Quick identification scan for vehicles (includes plates and visible marks). Focus on speed and basic detection.',
    estimated_processing_time: '30-60 seconds',
    use_case: 'Rapid preliminary analysis for large evidence batches'
  },

  configured_scan: {
    id: 'configured_scan',
    name: 'Configured Analysis', 
    description: 'User-selected model and scope with optimized single service',
    tier: 2,
    service_selection_strategy: 'user_selected_single',
    allow_model_selection: true,
    allow_target_selection: true,
    allow_analysis_depth: true,
    allow_custom_instructions: true,
    default_service: 'google-vertex-vision-pro', // Default to Pro for better quality
    targets: ['vehicles', 'people', 'text', 'activities'], // Full scope; vehicles includes plates and marks
    analysis_depth: 'Standard',
    max_cost_limit: 1.00,
    processing_priority: 'balanced',
    allow_user_customization: true,
    estimated_processing_time: '2-5 minutes',
    use_case: 'Standard forensic analysis with user control over parameters'
  },

  advanced_scan: {
    id: 'advanced_scan',
    name: 'Advanced Multi-Provider Analysis',
    description: 'Full multi-AI orchestration with granular service control',
    tier: 3,
    service_selection_strategy: 'multi_provider_orchestration',
    allow_provider_selection: true,
    allow_service_granularity: true,
    allow_model_per_provider: true,
    allow_task_specific_routing: true,
    supported_providers: ['google-vertex', 'aws-rekognition', 'openai-vision'],
    targets: ['vehicles', 'people', 'text', 'activities', 'faces', 'audio', 'documents'],
    analysis_depth: 'Comprehensive',
    max_cost_limit: 5.00,
    processing_priority: 'accuracy',
    allow_user_customization: true,
    enable_cross_validation: true, // Compare results between providers
    enable_confidence_scoring: true,
    estimated_processing_time: '5-15 minutes',
    use_case: 'Complex investigations requiring multiple AI perspectives and maximum accuracy'
  }
};

// Helper function to get scan tier configuration
const getScanTierConfig = (tierId) => {
  if (!SCAN_TIER_CONFIGURATIONS[tierId]) {
    throw new Error(`Scan tier ${tierId} not found in configuration`);
  }
  return SCAN_TIER_CONFIGURATIONS[tierId];
};

// Helper function to determine service selection based on tier
const getServicesForTier = (tierId, userSelections = {}) => {
  const tierConfig = getScanTierConfig(tierId);
  
  switch (tierConfig.service_selection_strategy) {
    case 'single_cheapest':
      return [{
        serviceId: tierConfig.forced_service,
        model: tierConfig.forced_model,
        rationale: 'Cheapest single service for quick analysis'
      }];
      
    case 'user_selected_single':
      return [{
        serviceId: userSelections.preferredService || tierConfig.default_service,
        model: userSelections.selectedModel || 'gemini-2.5-flash-lite',
        rationale: 'User-selected single service configuration'
      }];
      
    case 'multi_provider_orchestration':
      return userSelections.selectedServices || [{
        serviceId: 'google-vertex-vision-pro',
        model: userSelections.selectedModel || 'gemini-2.5-pro',
        rationale: 'Multi-provider orchestration (default to Pro service)'
      }];
      
    default:
      throw new Error(`Unknown service selection strategy: ${tierConfig.service_selection_strategy}`);
  }
};

// Map a modelId to the preferred Vertex Vision service tier
// Returns one of: 'google-vertex-vision-pro' | 'google-vertex-vision-flash' | 'google-vertex-vision-standard'
const mapModelToPreferredService = (modelId) => {
  if (!modelId || typeof modelId !== 'string') {
    return 'google-vertex-vision-standard';
  }
  const id = modelId.toLowerCase();
  // Any "pro" model → Pro service
  if (id.endsWith('-pro')) {
    return 'google-vertex-vision-pro';
  }
  // Flash (but not flash-lite) → Flash service
  if (id.includes('-flash') && !id.includes('-flash-lite')) {
    return 'google-vertex-vision-flash';
  }
  // Everything else (incl. flash-lite, 2.0 lite, etc.) → Standard service
  return 'google-vertex-vision-standard';
};

// Helper function to get model configuration
const getModelConfig = (modelId) => {
  for (const provider of Object.values(AI_MODELS)) {
    if (provider[modelId]) {
      return provider[modelId];
    }
  }
  throw new Error(`Model ${modelId} not found in configuration`);
};

// Helper function to build prompt for specific model
const buildPromptForModel = (modelId, targets, evidenceType, customInstructions = '', targetTiers = {}) => {
  const modelConfig = getModelConfig(modelId);
  return modelConfig.build_prompt(targets, evidenceType, customInstructions, targetTiers);
};

// Helper function to get all available models for frontend selection
const getAvailableModels = () => {
  const models = [];
  
  // Extract all Vertex AI models
  Object.keys(AI_MODELS.VERTEX_AI).forEach(modelKey => {
    const model = AI_MODELS.VERTEX_AI[modelKey];
    models.push({
      id: modelKey,
      name: model.name,
      provider: model.provider,
      speed: model.speed,
      accuracy: model.accuracy,
      cost_per_1k_tokens: model.cost_per_1k_tokens,
      best_for: model.best_for,
      capabilities: model.capabilities
    });
  });
  
  // Sort by cost (cheapest first) as default
  return models.sort((a, b) => a.cost_per_1k_tokens - b.cost_per_1k_tokens);
};

// Helper function to determine overall tier from target tiers
const determineOverallTier = (targetTiers = {}) => {
  const tiers = Object.values(targetTiers);
  if (tiers.length === 0) return 'professional'; // Default
  
  // If any tier is 'forensic', use forensic
  if (tiers.some(t => t?.toLowerCase() === 'forensic')) return 'forensic';
  // If any tier is 'professional', use professional
  if (tiers.some(t => t?.toLowerCase() === 'professional')) return 'professional';
  // Otherwise use standard
  return 'standard';
};

// Helper function to get the appropriate schema based on tier
const getV4SchemaForTier = (tier) => {
  const tierLower = (tier || 'professional').toLowerCase();
  switch (tierLower) {
    case 'standard':
      return RESPONSE_SCHEMAS.V4_STANDARD_TIER;
    case 'professional':
      return RESPONSE_SCHEMAS.V4_PROFESSIONAL_TIER;
    case 'forensic':
      return RESPONSE_SCHEMAS.V4_TARGET_CENTRIC; // Full forensic schema
    default:
      return RESPONSE_SCHEMAS.V4_PROFESSIONAL_TIER; // Default to professional
  }
};

// V4 TARGET-CENTRIC PROMPT BUILDER WITH TIER SUPPORT - Updated Oct 29, 2025
const buildV4TargetCentricPrompt = (targets = ['vehicles', 'environment', 'videographer'], customInstructions = '', targetTiers = {}) => {
  console.log(`🎯🎯🎯 TIER SYSTEM: buildV4TargetCentricPrompt called with targetTiers:`, JSON.stringify(targetTiers));
  
  // Determine the overall tier for schema selection
  const overallTier = determineOverallTier(targetTiers);
  console.log(`🎯🎯🎯 TIER SYSTEM: Overall tier determined as: ${overallTier}`);
  
  let prompt = FORENSIC_PROMPT_TEMPLATES.V4_SYSTEM_PROMPT + '\n\n';
  
  // Add target-specific prompts based on what's requested AND tier level
  if (targets.includes('vehicles')) {
    const tier = (targetTiers.vehicles || overallTier || 'professional').toLowerCase();
    const promptKey = `V4_VEHICLE_PROMPT_${tier.toUpperCase()}`;
    
    console.log(`🎯🎯🎯 TIER SYSTEM: Vehicle tier selected: ${tier}, promptKey: ${promptKey}`);
    
    // Use tier-specific prompt if available, otherwise fall back to base prompt
    if (FORENSIC_PROMPT_TEMPLATES[promptKey]) {
      prompt += '\n\n' + FORENSIC_PROMPT_TEMPLATES[promptKey];
      console.log(`✅✅✅ TIER SYSTEM: Using ${tier} tier vehicle prompt: ${promptKey}`);
    } else {
      prompt += '\n\n' + FORENSIC_PROMPT_TEMPLATES.V4_VEHICLE_PROMPT;
      console.log(`⚠️⚠️⚠️ TIER SYSTEM: Falling back to base vehicle prompt (tier not found: ${tier})`);
    }
  }
  
  if (targets.includes('environment')) {
    const tier = (targetTiers.environment || overallTier || 'professional').toLowerCase();
    const promptKey = `V4_ENVIRONMENT_PROMPT_${tier.toUpperCase()}`;
    
    console.log(`🎯🎯🎯 TIER SYSTEM: Environment tier selected: ${tier}, promptKey: ${promptKey}`);
    
    if (FORENSIC_PROMPT_TEMPLATES[promptKey]) {
      prompt += '\n\n' + FORENSIC_PROMPT_TEMPLATES[promptKey];
      console.log(`✅✅✅ TIER SYSTEM: Using ${tier} tier environment prompt: ${promptKey}`);
    } else {
      prompt += '\n\n' + FORENSIC_PROMPT_TEMPLATES.V4_ENVIRONMENT_PROMPT;
      console.log(`⚠️⚠️⚠️ TIER SYSTEM: Falling back to base environment prompt (tier not found: ${tier})`);
    }
  }
  
  if (targets.includes('videographer')) {
    const tier = (targetTiers.videographer || overallTier || 'professional').toLowerCase();
    const promptKey = `V4_VIDEOGRAPHER_PROMPT_${tier.toUpperCase()}`;
    
    console.log(`🎯🎯🎯 TIER SYSTEM: Videographer tier selected: ${tier}, promptKey: ${promptKey}`);
    
    if (FORENSIC_PROMPT_TEMPLATES[promptKey]) {
      prompt += '\n\n' + FORENSIC_PROMPT_TEMPLATES[promptKey];
      console.log(`✅✅✅ TIER SYSTEM: Using ${tier} tier videographer prompt: ${promptKey}`);
    } else {
      prompt += '\n\n' + FORENSIC_PROMPT_TEMPLATES.V4_VIDEOGRAPHER_PROMPT;
      console.log(`⚠️⚠️⚠️ TIER SYSTEM: Falling back to base videographer prompt (tier not found: ${tier})`);
    }
  }
  
  // Add custom instructions if provided
  if (customInstructions) {
    prompt += `\n\nADDITIONAL INSTRUCTIONS:\n${customInstructions}`;
  }
  
  // Get the appropriate schema for the tier
  const schema = getV4SchemaForTier(overallTier);
  
  // Add output format instructions with tier-appropriate schema
  prompt += `

CONFIDENCE SCORING REQUIREMENTS:
- 0.9-1.0: Clearly visible, unambiguous identification
- 0.7-0.8: Clearly visible with minor uncertainty
- 0.5-0.6: Partially visible or obscured
- 0.3-0.4: Barely visible, high uncertainty
- 0.0-0.2: Cannot determine reliably

CRITICAL OUTPUT REQUIREMENTS:
1. Analysis tier: ${overallTier.toUpperCase()}
2. For each observation, provide confidence scores (0.0 to 1.0)
3. Use empty arrays [] for undetectable elements, "unknown" for uncertain strings
4. Include timestamps for all observations in HH:MM:SS format
5. ONLY include targets that were explicitly requested in the analysis
6. forensic_summary field: Write a 1-3 sentence narrative summary describing what happened in the video as if explaining to a lawyer or jury. Example: "A gray GMC Sierra tow truck is parked in an outdoor lot during rainy weather. The video captures the vehicle from a handheld camera with moderate shake and stability issues."
${overallTier !== 'standard' ? '7. For relationships, provide bidirectional perspectives when relevant' : ''}

Note: Your response will be automatically validated against the ${overallTier} tier schema.
Begin forensic analysis:`;

  return prompt;
};

// Export all configurations
const aiModelsConfig = {
  AI_MODELS,
  ANALYSIS_TEMPLATES,
  SCAN_TIER_CONFIGURATIONS,
  FORENSIC_PROMPT_TEMPLATES,
  FORENSIC_TARGETS,
  RESPONSE_SCHEMAS,
  V4_STANDARD_TIER_SCHEMA,
  V4_PROFESSIONAL_TIER_SCHEMA,
  mapModelToPreferredService,
  getModelConfig,
  buildPromptForModel,
  getAvailableModels,
  getScanTierConfig,
  getServicesForTier,
  buildV4TargetCentricPrompt,
  determineOverallTier,
  getV4SchemaForTier
};



// Export all configurations
export const FORENSIC_PROMPT_TEMPLATES_EXPORT = FORENSIC_PROMPT_TEMPLATES;
export const AI_MODELS_EXPORT = AI_MODELS;
export const ANALYSIS_TEMPLATES_EXPORT = ANALYSIS_TEMPLATES;
export const RESPONSE_SCHEMAS_EXPORT = RESPONSE_SCHEMAS;

// Named exports for individual access
export { FORENSIC_PROMPT_TEMPLATES };
export { RESPONSE_SCHEMAS };
export { AI_MODELS };
export { ANALYSIS_TEMPLATES };
export { getModelConfig };
export { buildPromptForModel };
// Also export frontend helpers used by UI
export { getAvailableModels };
export { getScanTierConfig };
export { getServicesForTier };
export { mapModelToPreferredService };

// Export default configuration
const aiConfig = {
  AI_MODELS,
  ANALYSIS_TEMPLATES,
  SCAN_TIER_CONFIGURATIONS,
  FORENSIC_PROMPT_TEMPLATES,
  FORENSIC_TARGETS,
  RESPONSE_SCHEMAS,
  mapModelToPreferredService,
  getModelConfig,
  buildPromptForModel,
  getAvailableModels,
  getScanTierConfig,
  getServicesForTier
};

export default aiConfig;